<template>
    <nav
        v-touch="{
            left: swipeToCollapse,
        }"
        class="ms-side-navbar"
        :class="[
            `side-nav--${mode}`,
            expanded ? 'side-nav--expanded' : 'side-nav--collapsed',
            {
                'side-nav--pinned': pinned,
            },
        ]"
        @click.stop
    >
        <div class="side-menu-panel">
            <div class="logo-container side-menu-logo-row">
                <button
                    v-if="mode === 'mobile'"
                    type="button"
                    class="side-menu-mobile-button"
                    aria-label="Close navigation"
                    @click.stop="emit('toggleMenu')"
                >
                    <font-awesome-icon icon="fa-solid fa-close" />
                </button>
                <img
                    v-if="globalState.customLogo && globalState.whitelabel"
                    :src="globalState.customLogo"
                    alt="custom logo"
                    class="logo menu-collapse"
                    fetchpriority="high"
                />
                <img
                    v-else
                    src="@/assets/svg/ms-logo-icon-new.svg"
                    alt="MemoryShare Logo"
                    class="logo"
                    fetchpriority="high"
                />
                <button
                    v-if="mode === 'desktop' && expanded"
                    type="button"
                    class="side-menu-pin-button"
                    :class="{ 'side-menu-pin-button--active': pinned }"
                    :aria-label="pinButtonLabel"
                    :title="pinButtonLabel"
                    @click.stop="emit('toggleMenu')"
                >
                    <font-awesome-icon icon="fa-solid fa-thumbtack" />
                </button>
                <v-menu v-if="mode === 'mobile'" offset-y>
                    <template #activator="{ props: menuProps }">
                        <button
                            v-bind="menuProps"
                            type="button"
                            class="side-menu-mobile-button side-menu-mobile-profile-button"
                            aria-label="Open profile menu"
                            @click.stop
                        >
                            <font-awesome-icon icon="fa-regular fa-circle-user" size="2xl" />
                        </button>
                    </template>
                    <v-list class="dropdown">
                        <v-list-item v-if="canAccessSettings" @click="goToSettings">
                            <font-awesome-icon icon="fa-regular fa-gear" class="mr-4" />
                            <span>Settings</span>
                        </v-list-item>
                        <v-list-item v-if="canManageUsers" @click="getUsers">
                            <font-awesome-icon icon="fa-regular fa-circle-user" class="mr-4" />
                            <span>Manage Users</span>
                        </v-list-item>
                        <v-list-item v-if="canEditProfile" @click="getEditInfo">
                            <font-awesome-icon icon="fa-regular fa-circle-user" class="mr-4" />
                            <span>Edit Profile</span>
                        </v-list-item>
                        <v-list-item @click="logout">
                            <font-awesome-icon icon="fa-solid fa-right-from-bracket" class="mr-4" />
                            <span>Logout</span>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>

            <div v-if="!isSupplier" class="side-menu-quick-links">
                <ul class="ms-side-navbar-link-container side-menu-primary-links">
                    <router-link
                        v-if="isFuneralHome"
                        :to="{ name: 'CreateService' }"
                        class="side-menu-link"
                        @click="emit('navigate')"
                    >
                        <li class="menu-entry">
                            <div class="menu-entry-icon">
                                <font-awesome-icon icon="fa-solid fa-cloud-arrow-up" fw />
                            </div>
                            <span class="side-menu-label">Create New Service</span>
                        </li>
                    </router-link>
                    <custom-tooltip
                        class="tooltip"
                        :disable="mode !== 'desktop' || expanded"
                        :tooltip-props="{ right: true }"
                    >
                        <template #activator>
                            <router-link
                                :to="{ name: 'LiveDashboard' }"
                                class="side-menu-link"
                                @click="emit('navigate')"
                            >
                                <li class="menu-entry">
                                    <div class="menu-entry-icon">
                                        <font-awesome-icon icon="fa-solid fa-play" />
                                    </div>
                                    <strong class="side-menu-label"><span>Live Dashboard</span></strong>
                                </li>
                            </router-link>
                        </template>

                        <template #content>
                            <span>Live Dashboard</span>
                        </template>
                    </custom-tooltip>
                </ul>
            </div>

            <div class="side-menu-divider">
                <v-divider />
            </div>
            <div class="side-menu-scroll">
                <ul class="ms-side-navbar-link-container">
                    <div
                        v-for="(entry, index) in filteredMenuData"
                        :key="index"
                        class="side-menu-link"
                        @mousedown.stop
                        @mousedown.middle="handleMiddle(entry, $event)"
                        @click="handleNavigate(entry)"
                    >
                        <custom-tooltip :disable="mode !== 'desktop' || expanded" :tooltip-props="{ right: true }">
                            <template #activator>
                                <li class="menu-entry" :class="{ selected: determineIfSelected(entry) }">
                                    <div
                                        class="menu-entry-icon"
                                        :class="[entry.label === 'Wrapped' ? 'heartBeat' : '']"
                                    >
                                        <font-awesome-icon :icon="entry.icon" />
                                    </div>
                                    <span class="side-menu-label">{{ entry.label }}</span>
                                    <v-badge
                                        v-if="mode === 'desktop' && !expanded && entry.isNew"
                                        color="ms-orange"
                                        dot
                                        class="side-menu-new-badge"
                                    />
                                    <template v-else-if="entry.isNew">
                                        <v-spacer />
                                        <v-chip color="ms-orange" variant="flat" label size="small">new</v-chip>
                                    </template>
                                </li>
                            </template>

                            <template #content>
                                <span>{{ entry.label }}</span>
                            </template>
                        </custom-tooltip>
                    </div>
                </ul>
            </div>
            <div class="side-menu-profile-dialogs">
                <UsersTable ref="usersTable" />
                <EditProfileTable ref="editProfileTable" />
            </div>
        </div>
    </nav>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { RouteParamsRawGeneric } from 'vue-router';
import { generateMenu } from '@/configs/mainMenu';
import CustomTooltip from '@/components/ui/CustomTooltip.vue';
import { useGlobalState } from '@/stores/globalState';
import UsersTable from '@/components/Tables/UsersTable.vue';
import EditProfileTable from '@/components/Tables/EditProfileTable.vue';

interface SideMenuDialogRef {
    openDialog: boolean;
    getUsers: (includeSelf: boolean) => void;
}

interface SideMenuAuth {
    role: string[];
    impersonating?: boolean;
    session?: {
        owner?: {
            displayTerms?: boolean;
        };
        supplier?: {
            id: string | number;
        };
    };
    user?: Record<string, unknown>;
    funeralHomeId?: string | number | null;
    logout?: (options: { logoutParams: { returnTo: string } }) => void;
}

interface SideMenuEntry {
    roles: string[];
    to: string;
    params: RouteParamsRawGeneric;
    label: string;
    icon: string;
    selectedWhen: string[];
    isNew?: boolean;
}

interface SideMenuProxy {
    $auth?: SideMenuAuth;
}

interface SwipeEvent {
    offsetX: number;
}

type SideNavMode = 'desktop' | 'tablet' | 'mobile';

const props = defineProps<{
    mode: SideNavMode;
    expanded: boolean;
    pinned: boolean;
}>();

const emit = defineEmits<{
    toggleMenu: [];
    navigate: [];
}>();

const instance = getCurrentInstance();
const route = useRoute();
const router = useRouter();
const globalState = useGlobalState();
const usersTable = ref<SideMenuDialogRef | null>(null);
const editProfileTable = ref<SideMenuDialogRef | null>(null);

const auth = computed(() => {
    const proxy = instance?.proxy as SideMenuProxy | null;
    return proxy?.$auth;
});

const roles = computed(() => auth.value?.role ?? []);
const isSupplier = computed(() => roles.value.includes('Supplier'));
const isFuneralHome = computed(() => roles.value.includes('FuneralHome'));
const pinButtonLabel = computed(() => (props.pinned ? 'Unpin navigation' : 'Pin navigation'));
const canAccessSettings = computed(
    () => !roles.value.includes('SuperAdmin') && !roles.value.includes('RegionalManager'),
);
const canManageUsers = computed(() => roles.value.includes('FuneralHome') || roles.value.includes('Supplier'));
const canEditProfile = computed(
    () => !roles.value.includes('SuperAdmin') && !roles.value.includes('Supplier') && !auth.value?.impersonating,
);

const filteredMenuData = computed(() => {
    const currentAuth = auth.value;

    if (!currentAuth) {
        return [];
    }

    const generatedMenu: unknown = generateMenu(currentAuth);

    if (!Array.isArray(generatedMenu)) {
        return [];
    }

    return generatedMenu.filter(isVisibleMenuEntry);
});

function isSideMenuEntry(entry: unknown): entry is SideMenuEntry {
    if (!entry || typeof entry !== 'object') {
        return false;
    }

    return (
        'roles' in entry &&
        Array.isArray(entry.roles) &&
        'to' in entry &&
        typeof entry.to === 'string' &&
        'params' in entry &&
        !!entry.params &&
        typeof entry.params === 'object' &&
        'label' in entry &&
        typeof entry.label === 'string' &&
        'icon' in entry &&
        typeof entry.icon === 'string' &&
        'selectedWhen' in entry &&
        Array.isArray(entry.selectedWhen)
    );
}

function isVisibleMenuEntry(entry: unknown): entry is SideMenuEntry {
    if (!isSideMenuEntry(entry)) {
        return false;
    }

    const currentAuth = auth.value;

    if (!currentAuth) {
        return false;
    }

    if (entry.to === 'TermsAndConditions' && !currentAuth.session?.owner?.displayTerms) {
        return false;
    }

    if (entry.to === 'GoogleReport' && !globalState.displayKeywordReportNav) {
        return false;
    }

    if (entry.to === 'Guestbook' && !globalState.displayGuestbookNav) {
        return false;
    }

    return entry.roles.some(role => roles.value.includes(role));
}

function determineIfSelected(entry: SideMenuEntry): boolean {
    return typeof route.name === 'string' && entry.selectedWhen.includes(route.name);
}

function handleMiddle(entry: SideMenuEntry, event: MouseEvent) {
    event.preventDefault();

    if (event.button !== 1) {
        return;
    }

    const url = router.resolve({ name: entry.to, params: entry.params });
    window.open(url.href, '_blank');
}

function handleNavigate(entry: SideMenuEntry) {
    if (route.name === entry.to) {
        emit('navigate');
        return;
    }

    if (entry.to === 'TermsAndConditions') {
        const url = router.resolve({ name: entry.to, params: entry.params });
        window.open(url.href, '_blank');
        emit('navigate');
        return;
    }

    if (entry.label === 'PowerBi') {
        window.open(entry.to, '_blank');
        emit('navigate');
        return;
    }

    if (entry.to.includes('http')) {
        window.open('https://intercom.help/memory-share/', '_blank');
        emit('navigate');
        return;
    }

    router.push({ name: entry.to, params: entry.params });
    window.localStorage.removeItem('memoryshare-local-settings');
    emit('navigate');
}

function goToSettings() {
    const currentAuth = auth.value;

    if (!currentAuth) {
        return;
    }

    if (roles.value.includes('Owner')) {
        const ownerId = currentAuth.user?.['https://memoryshare.com/owner_id'];

        if (typeof ownerId === 'string' || typeof ownerId === 'number') {
            router.push(`/owner-settings/${ownerId}?tab=details`);
            emit('navigate');
        }

        return;
    }

    if (roles.value.includes('Supplier')) {
        const supplierId = currentAuth.session?.supplier?.id;

        if (typeof supplierId === 'string' || typeof supplierId === 'number') {
            router.push(`/suppliers/edit/${supplierId}`);
            emit('navigate');
        }

        return;
    }

    router.push('/settings');
    emit('navigate');
}

function getEditInfo() {
    if (!editProfileTable.value) {
        return;
    }

    editProfileTable.value.openDialog = true;
    editProfileTable.value.getUsers(true);
}

function getUsers() {
    if (!usersTable.value) {
        return;
    }

    usersTable.value.openDialog = true;
    usersTable.value.getUsers(true);
}

function logout() {
    auth.value?.logout?.({
        logoutParams: {
            returnTo: window.location.origin,
        },
    });
}

function swipeToCollapse(event: SwipeEvent) {
    const { offsetX } = event;

    if (Math.abs(offsetX) > 80) {
        emit('toggleMenu');
    }
}
</script>

<style scoped>
.tooltip {
    width: 2.75rem;
}
.ms-side-navbar {
    --side-nav-rail-width: 4rem;
    --side-nav-icon-column-width: calc(var(--side-nav-rail-width) - 1rem);
    --side-nav-panel-width: 300px;

    align-self: stretch;
    flex: 0 0 auto;
    height: auto;
    min-height: 0;
    position: relative;
    transition:
        transform 0.15s ease,
        width 0.15s ease,
        left 0.15s ease;
    width: var(--side-nav-rail-width);
    z-index: 1100;
}

.ms-side-navbar.side-nav--pinned {
    width: var(--side-nav-panel-width);
}

.ms-side-navbar.side-nav--expanded:not(.side-nav--pinned) {
    z-index: 1100;
}

.side-menu-panel {
    background: white;
    box-shadow: 0 0.15rem 1.5rem 0 rgba(31, 45, 65, 0.15);
    display: flex;
    flex-direction: column;
    height: 100%;
    left: 0;
    min-height: 0;
    overflow: hidden;
    position: absolute;
    top: 0;
    transition: width 0.15s ease;
    width: var(--side-nav-rail-width);
}

.ms-side-navbar.side-nav--expanded .side-menu-panel {
    width: var(--side-nav-panel-width);
}

.logo-container {
    align-items: center;
    display: flex;
    gap: 0.75rem;
    height: 4.5rem;
    justify-content: space-between;
    padding: 1rem 0.75rem;
}

.logo {
    flex: 0 0 auto;
    height: 2.25rem;
    max-width: 2.5rem;
    min-width: 2.5rem;
    object-fit: contain;
    width: 2.5rem;
}

.menu-collapse.logo {
    max-width: 2.5rem;
}

.side-menu-quick-links {
    margin-top: 4rem;
    padding: 0 0.5rem;
}

.side-menu-primary-links {
    flex: 0;
}

.side-menu-link {
    text-decoration: none;
}

.side-menu-divider {
    padding: 0 1rem;
}

.side-menu-scroll {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 1rem 0.5rem;
    scrollbar-gutter: hidden;
}

.ms-side-navbar-link-container {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.75rem;
    list-style-type: none;
    margin: 0;
    min-height: 0;
    padding: 0;
}

.menu-entry {
    align-items: center;
    border-radius: 5px;
    cursor: pointer;
    display: grid;
    font-size: 1rem;
    gap: 0.5rem;
    grid-template-columns: var(--side-nav-icon-column-width) minmax(0, 1fr) auto;
    min-height: 2.75rem;
    padding: 0;
    overflow: hidden;
    position: relative;
    transition:
        background-color 0.3s ease-in-out,
        color 0.3s ease-in-out;
    width: calc(var(--side-nav-panel-width) - 1rem);
}

.menu-entry:hover {
    background: #eee;
}

.menu-entry.selected {
    background: transparent;
    color: white;
}

.menu-entry.selected::before {
    background: #ff7a1a;
    border-radius: 5px;
    content: '';
    height: 2.75rem;
    left: calc((var(--side-nav-icon-column-width) - 2.75rem) / 2);
    position: absolute;
    top: 0;
    transition: width 0.15s ease;
    width: calc(100% - ((var(--side-nav-icon-column-width) - 2.75rem) / 2));
    z-index: 0;
}

.menu-entry.selected .menu-entry-icon {
    color: white;
}

.menu-entry > * {
    position: relative;
    z-index: 1;
}

.ms-side-navbar.side-nav--expanded .menu-entry.selected .menu-entry-icon {
    background: transparent;
}

.menu-entry-icon {
    align-items: center;
    border-radius: 5px;
    display: flex;
    height: 2.75rem;
    justify-content: center;
    justify-self: center;
    transition:
        background-color 0.3s ease-in-out,
        color 0.3s ease-in-out;
    width: 2.75rem;
}

.side-menu-label {
    min-width: 0;
    opacity: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: opacity 0.12s ease;
    white-space: nowrap;
}

.ms-side-navbar.side-nav--collapsed .side-menu-label {
    opacity: 0;
    pointer-events: none;
}

.ms-side-navbar.side-nav--collapsed .side-menu-scroll {
    scrollbar-width: none;
}

.ms-side-navbar.side-nav--collapsed .side-menu-scroll::-webkit-scrollbar {
    display: none;
}

.ms-side-navbar.side-nav--collapsed .menu-entry {
    background: transparent;
    color: inherit;
}

.ms-side-navbar.side-nav--collapsed .menu-entry.selected {
    background: transparent;
    color: white;
}

.ms-side-navbar.side-nav--collapsed .menu-entry.selected::before {
    width: 2.75rem;
}

.ms-side-navbar.side-nav--collapsed .menu-entry:hover .menu-entry-icon {
    background: #eee;
}

.ms-side-navbar.side-nav--collapsed .menu-entry.selected .menu-entry-icon {
    background: transparent;
    color: white;
}

.side-menu-new-badge {
    margin-top: -8px;
}

.side-menu-pin-button {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 999px;
    color: #60656f;
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 auto;
    height: 2.25rem;
    justify-content: center;
    transition:
        background-color 0.15s ease,
        color 0.15s ease,
        transform 0.15s ease;
    width: 2.25rem;
}

.side-menu-pin-button:hover,
.side-menu-pin-button:focus-visible {
    background: #f1f4f7;
    color: #1f2d41;
}

.side-menu-pin-button:focus-visible {
    outline: 2px solid #ff7a1a;
    outline-offset: 2px;
}

.side-menu-pin-button--active {
    background: #fff3eb;
    color: #ff7a1a;
    transform: rotate(-25deg);
}

.side-menu-mobile-button {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 999px;
    color: #1f2d41;
    cursor: pointer;
    display: inline-flex;
    height: 2.75rem;
    justify-content: center;
    padding: 0;
    width: 2.75rem;
}

.side-menu-mobile-button:hover,
.side-menu-mobile-button:focus-visible {
    background: #f1f4f7;
}

.side-menu-mobile-button:focus-visible {
    outline: 2px solid #ff7a1a;
    outline-offset: 2px;
}

.side-menu-profile-dialogs {
    position: absolute;
}

/* source animate.css */
@keyframes heartBeat {
    0% {
        transform: scale(1);
    }

    14% {
        transform: scale(1.1);
    }

    28% {
        transform: scale(1);
    }

    42% {
        transform: scale(1.1);
    }

    70% {
        transform: scale(1);
    }
}

.heartBeat {
    animation-name: heartBeat;
    animation-timing-function: ease-in-out;
    animation-duration: 1.5s;
    animation-fill-mode: both;
    animation-iteration-count: infinite;
    animation-delay: 5s;
}

.ms-side-navbar.side-nav--tablet.side-nav--collapsed {
    width: var(--side-nav-rail-width);
}

.ms-side-navbar.side-nav--tablet.side-nav--expanded {
    width: var(--side-nav-rail-width);
    z-index: 1200;
}

.ms-side-navbar.side-nav--tablet.side-nav--expanded .side-menu-panel {
    width: var(--side-nav-panel-width);
}

.ms-side-navbar.side-nav--tablet.side-nav--expanded .side-menu-label {
    opacity: 1;
    pointer-events: auto;
}

@media only screen and (max-width: 768px) {
    .ms-side-navbar.side-nav--mobile {
        --side-nav-icon-column-width: 3rem;
        --side-nav-panel-width: 100vw;

        bottom: 0;
        height: auto;
        left: 0;
        position: fixed;
        top: 0;
        transform: translateX(-100%);
        width: var(--side-nav-rail-width) !important;
        z-index: 1200;
    }

    .ms-side-navbar.side-nav--mobile.side-nav--expanded {
        transform: translateX(0);
        --side-nav-panel-width: 100vw !important;
    }

    .ms-side-navbar.side-nav--mobile .side-menu-panel {
        width: var(--side-nav-rail-width) !important;
    }

    .menu-entry {
        width: calc(100vw - 1rem);
    }

    .ms-side-navbar.side-nav--mobile .logo-container {
        display: grid;
        grid-template-columns: 2.75rem 1fr 2.75rem;
        justify-content: stretch;
        margin: 0;
        max-width: none;
        padding: 1rem;
        width: 100%;
    }

    .ms-side-navbar.side-nav--mobile .logo {
        justify-self: center;
    }

    .side-menu-pin-button {
        display: none;
    }

    .ms-side-navbar.side-nav--mobile .side-menu-label {
        opacity: 1;
        pointer-events: auto;
    }

    .ms-side-navbar.side-nav--mobile.side-nav--expanded .menu-entry {
        background: transparent;
        color: inherit;
        width: calc(100vw - 1rem);
    }

    .ms-side-navbar.side-nav--mobile.side-nav--expanded .menu-entry.selected {
        background: #ff7a1a;
        color: white;
    }

    .ms-side-navbar.side-nav--mobile.side-nav--collapsed .menu-entry:hover .menu-entry-icon,
    .ms-side-navbar.side-nav--mobile.side-nav--collapsed .menu-entry.selected .menu-entry-icon {
        background: transparent;
        color: inherit;
    }

    .ms-side-navbar.side-nav--mobile.side-nav--collapsed .menu-entry.selected {
        background: #ff7a1a;
        color: white;
    }
}
</style>
